import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { devicesTableQuery, type ListName } from '../api/proactivitee'
import { DeviceWorkspace } from '../devices/DeviceWorkspace'
import { Content } from '../ui/Shell'
import { DataTable, useTableState } from '../ui/DataTable'
import { deviceColumns, extendedColumns, serverColumns, type DeviceRow } from './columns'
import { useLiveBatch } from './liveBatch'

/**
 * Listele Proactivitee - All devices, Workstations, Servers, Network,
 * Mobiles, Extended. Acelasi tabel (`#proactiviteeWorkstations` etc.),
 * aceleasi coloane ca ProactiviteeDevicesModel / ProactiviteeServersModel /
 * ProactiviteeExtendedModel.
 *
 * Extended isi are modelul lui: alte coloane si alta ordonare implicita
 * (Group, descrescator), fiindca ecranul raspunde la alta intrebare - cine
 * are Extended Monitoring pornit si ce se intampla pe el - nu la "ce masini
 * am". Pana acum desena lista tuturor masinilor.
 */

export const TABLE_ID: Record<ListName, string> = {
  all: 'proactiviteeDevices',
  workstations: 'proactiviteeWorkstations',
  servers: 'proactiviteeServers',
  network: 'proactiviteeNetworkDevices',
  mobiles: 'proactiviteeMobiles',
  extended: 'proactiviteeExtended',
}

export function DeviceListPage({ list }: { list: ListName }) {
  const id = TABLE_ID[list]
  const isServers = list === 'servers'
  const isExtended = list === 'extended'
  /* order: [[5, 'desc']] in ProactiviteeExtendedModel - Group. */
  const [state, setState] = useTableState(id, { start: 0, length: 25, order: { column: isExtended ? 5 : 2, dir: 'desc' }, search: '' })
  const columns = useMemo(
    () => (isExtended ? extendedColumns() : isServers ? serverColumns() : deviceColumns()),
    [isExtended, isServers]
  )
  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[2]
  const query = useQuery({
    ...devicesTableQuery({
      list,
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: orderColumn.index,
      orderName: orderColumn.name ?? '',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })
  const rows = (query.data?.data ?? []) as DeviceRow[]
  /* Cifrele vii pe randurile vazute, si agentul la 1 s cat e lista deschisa
     (Proactivitee::getLivePerformanceBatch). Nu pe Extended: acolo coloanele
     sunt altele. */
  useLiveBatch(id, rows.map((r) => Number(r.deviceId)), !isExtended)

  return (
    <Content>
      <div id="content">
        <DataTable
          id={id}
          columnTools
          columns={columns}
          rows={rows}
          total={query.data?.recordsTotal ?? 0}
          fetching={query.isFetching}
          error={query.isError ? (query.error as Error).message : null}
          onRetry={() => void query.refetch()}
          state={state}
          onState={setState}
          rowId={(r) => String(r.DT_RowId)}
          details={(row) => <DeviceWorkspace id={Number(row.deviceId)} from="proactivitee" />}
          /* `buttons: DataTablesProperties.buttons` pe fiecare lista din
             proactivitee.js: Copy, Columns si Refresh, in subsol. Lipseau pe
             tot modulul, desi celelalte module portate le au. */
          footerButtons={{ copy: true, colvis: true, refresh: () => void query.refetch() }}
        />
      </div>
    </Content>
  )
}
